import React from 'react';
import {
  CheckCircle,
  Loader,
  XCircle,
  Clock,
  StopCircle,
  PauseCircle,
  ClipboardCheck,
  AlertCircle,
} from 'lucide-react';
import type { ProcessingJob } from '../types';

type JobStatus = ProcessingJob['status'];

/**
 * Five tones, not nine colours.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * This used to give each of the nine statuses its own hue: green, blue, red,
 * yellow, purple, gray, amber, violet, amber. Three of those — yellow
 * "Pending", purple "Queued", violet "Awaiting Validation" — all mean "no
 * result yet", so the palette was asking people to learn nine colours that
 * encoded five states, and the two ambers were already ambiguous.
 *
 * Statuses now collapse onto the five tones the product actually has, and the
 * label plus the icon tell the nine apart. Colour answers "do I need to do
 * something?"; the label answers "what exactly?".
 */
export type StatusTone = 'pass' | 'fail' | 'review' | 'active' | 'inert';

const TONE_CLASSES: Record<StatusTone, string> = {
  pass: 'qc-success-bg qc-success',
  fail: 'qc-error-bg qc-error',
  review: 'qc-warning-bg qc-warning',
  active: 'qc-info-bg qc-info',
  inert: 'bg-bg-subtle text-text-secondary',
};

/** Maps the tone onto the shared `.status-edge-*` modifier so a row, a card
 *  and a badge all mark the same job the same way. */
export const TONE_EDGE: Record<StatusTone, string> = {
  pass: 'status-edge-pass',
  fail: 'status-edge-fail',
  review: 'status-edge-review',
  active: 'status-edge-active',
  inert: '',
};

interface BadgeConfig {
  label: string;
  tone: StatusTone;
  icon: React.ReactNode;
}

const BADGE_CONFIG: Record<JobStatus, BadgeConfig> = {
  completed: {
    label: 'Completed',
    tone: 'pass',
    icon: <CheckCircle className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  processing: {
    label: 'Processing',
    tone: 'active',
    icon: <Loader className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />,
  },
  failed: {
    label: 'Failed',
    tone: 'fail',
    icon: <XCircle className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  pending: {
    label: 'Pending',
    tone: 'active',
    icon: <Clock className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  queued: {
    label: 'Queued',
    tone: 'active',
    icon: <Clock className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'inert',
    icon: <StopCircle className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  suspended: {
    label: 'Paused',
    tone: 'review',
    icon: <PauseCircle className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  awaiting_validation: {
    label: 'Awaiting Validation',
    tone: 'review',
    icon: <ClipboardCheck className="w-3.5 h-3.5" aria-hidden="true" />,
  },
  completed_with_errors: {
    label: 'Completed with Errors',
    tone: 'review',
    icon: <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />,
  },
};

const FALLBACK: BadgeConfig = {
  label: 'Unknown',
  tone: 'inert',
  icon: <Clock className="w-3.5 h-3.5" aria-hidden="true" />,
};

/** The tone for a status, for callers that want to mark a whole row. */
export const jobStatusTone = (status: JobStatus): StatusTone =>
  (BADGE_CONFIG[status] ?? FALLBACK).tone;

interface JobStatusBadgeProps {
  status: JobStatus;
  /** Use 'sm' for job list rows, 'md' (default) for detail pages. */
  size?: 'sm' | 'md';
}

/**
 * Renders a consistent status badge for a ProcessingJob.
 * Centralises badge config so all nine statuses stay in sync.
 */
export const JobStatusBadge: React.FC<JobStatusBadgeProps> = ({
  status,
  size = 'md',
}) => {
  const config = BADGE_CONFIG[status] ?? FALLBACK;

  const sizeClass =
    size === 'sm'
      ? 'px-2 py-0.5 text-xs rounded'
      : 'px-2.5 py-1 text-sm rounded-md';

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium whitespace-nowrap ${sizeClass} ${TONE_CLASSES[config.tone]}`}
      data-testid={`status-badge-${status}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
};
